import type { ComponentChildren } from 'preact'
import { ExternalLink, Video } from 'lucide-preact'
import type { OnlineEventViewDto } from '@/api/webs'
import { CopyButton } from '@/components/common/CopyButton'
import { routes } from '@/utils/routes'
import { LINE_BUTTON } from './panels/room/roomStyles'
import {
    ROOM_ENTRY_OPENS_MINUTES_BEFORE_START, formatWallClockDay, formatWallClockTime, roomEntryOpens, roomHasReplay, roomViewerLink,
    sessionEnd, sessionHasEnded,
} from '@/utils/webinarRoomSetup'

interface WebRoomLineProps {
    event: OnlineEventViewDto
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
    /** The Re-run button (ORB-1065), after the room's own actions. */
    rerunButton?: ComponentChildren
}

/**
 * What the line says: when viewers can enter, or how the session ended once it has. "Ended" is only this browser's
 * clock against the scheduled end; the replay goes out when the scheduler publishes it, a minute or more later, so
 * the line says what the replay link will do rather than that the replay is live.
 */
function roomLineText(event: OnlineEventViewDto, ended: boolean): string {
    const end = sessionEnd(event.EndDateTime)
    if (ended && end) {
        return `Ended ${formatWallClockTime(end)}. ${roomHasReplay(event)
            ? 'The replay link plays the recording once it’s published, usually within a minute.'
            : 'No replay from OrbitalX (Mode: Upcoming).'}`
    }
    const entry = roomEntryOpens(event.StartDateTime)
    return entry
        ? `Viewers can enter from ${formatWallClockTime(entry)} on ${formatWallClockDay(entry)}.`
        : `Viewers can enter ${ROOM_ENTRY_OPENS_MINUTES_BEFORE_START} minutes before the start.`
}

/**
 * The header's line for an OrbitalX-room web (ORB-1086): when viewers can enter, Open room, and the link to
 * hand out. The viewer link exists from the first sign-off; after an ended Hybrid session it plays the
 * replay, and after an ended Upcoming one it plays nothing, so there is nothing to copy.
 */
export function WebRoomLine({ event, clientCompanyId, workspaceId, campaignId, webId, rerunButton }: WebRoomLineProps) {
    const viewerLink = roomViewerLink(event)
    const ended = !!viewerLink && sessionHasEnded(event, new Date())
    const roomUrl = `${routes.scoped(clientCompanyId, workspaceId).web(campaignId, webId)}/live`

    const copyLink = !viewerLink
        ? <CopyButton text="" label="Viewer link" variant="default" size="md" buttonText="Copy viewer link" disabledReason="Set when you sign off" />
        : !ended
            ? <CopyButton text={viewerLink} label="Viewer link" variant="default" size="md" buttonText="Copy viewer link" />
            : roomHasReplay(event)
                ? <CopyButton text={viewerLink} label="Replay link" variant="default" size="md" buttonText="Copy replay link" />
                : null

    return (
        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
            <p className="flex-1 min-w-[18rem] flex items-center gap-2.5 text-sm text-slate-600 dark:text-slate-300">
                <Video className="w-[18px] h-[18px] flex-none text-slate-400 dark:text-slate-500" />
                <span><b className="font-semibold text-slate-900 dark:text-white">OrbitalX room.</b> {roomLineText(event, ended)}</span>
            </p>
            <div className="flex flex-wrap items-center gap-2">
                <a
                    href={roomUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Opens the full-screen room in a new tab"
                    className={LINE_BUTTON}
                >
                    <ExternalLink className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                    Open room
                </a>
                {copyLink}
                {rerunButton}
            </div>
        </div>
    )
}
